import {useEffect, useMemo, useRef} from 'react';
import {useIntl} from 'react-intl';
import {convertFromHtmlToDraftJs} from './HtmlToDraftJsConverter';
import {convertFromDraftJsToHtml} from './DraftJsToHtmlConverter';
import {isEditorEmpty} from './isEditorEmpty';
import {wrapLooseInlineContent} from './wrapLooseInlineContent';
import {detectRoundTripDrift} from './roundTripDrift';
import {createToast} from '../toasts/another-toast/toaster';
import {
	type EditorSurface,
	trackEditorOpened,
	trackEditorSaveFailed,
	trackRoundTripDrift,
} from '../../../../tracking/amplitude/editorEvents';

type UseDraftJsHtmlConversionProps = {
	initialDraftJsContent: string;
	onDraftJsChanged?: (draftJsContent: string) => void;
	/**
	 * Which editor this is, for analytics. Omitted, the conversion still works and
	 * still reports failures to the user — only the events are skipped, because an
	 * event that cannot say which surface it came from cannot be acted on.
	 */
	surface?: EditorSurface;
	/**
	 * Reads the editor's own serialisation of its current content.
	 *
	 * Read once, before the user can type, to learn what an unedited blur looks like —
	 * see the save-attempt note in `convertHtmlToDraftJs`. Omitted, the conversion still
	 * works; every blur is then treated as an edit, so a failed conversion is reported
	 * whether or not anything changed.
	 */
	getEditorHtml?: () => string | undefined;
};

type UseDraftJsHtmlConversionReturn = {
	initialHtml: string;
	convertHtmlToDraftJs: (htmlContent: string) => void;
	/**
	 * Whether there may be an edit worth committing: `htmlContent` differs from what the
	 * editor held before the user could reach it, or a save has already been handed over —
	 * that save may have failed, so whether the stored content matches is then the
	 * consumer's to judge.
	 *
	 * The imperative save asks before it converts anything, because it fires on every close,
	 * including the ones where nobody touched the description. A blur applies the same test
	 * inside `convertHtmlToDraftJs`. Committing an unedited editor is not free: the DraftJS
	 * round trip adds `data:{}` to every block, and content stored as html never equals the
	 * DraftJS it converts to, so either slips past the consumer's unchanged-check and is
	 * rewritten.
	 */
	hasUnexportedChange: (htmlContent: string) => boolean;
};

export const useDraftJsHtmlConversion = ({
	initialDraftJsContent,
	onDraftJsChanged,
	surface,
	getEditorHtml,
}: UseDraftJsHtmlConversionProps): UseDraftJsHtmlConversionReturn => {
	const intl = useIntl();
	// The converter hands back what it was given when it cannot render it: html, plain text,
	// or a DraftJS document it failed on. That reaches the editor as stored, and stored
	// content need not keep its text inside paragraphs; the editor discards any that is not
	// on the first keystroke.
	const initialHtml = useMemo(() => {
		const storedHtml = convertFromDraftJsToHtml(initialDraftJsContent);
		return storedHtml === initialDraftJsContent ? wrapLooseInlineContent(storedHtml) : storedHtml;
	}, [initialDraftJsContent]);
	// Drift is a property of the stored content, so it cannot change between two blurs
	// of the same editor. Checking once keeps a second full round trip off every blur.
	const driftChecked = useRef(false);
	// What an unedited blur hands back: the editor's own serialisation, read once before
	// the user can reach it and never moved. Boxed so that "not captured yet" and
	// "captured, and the editor gave us nothing" stay distinguishable — the second has to
	// stop the capture from running again on every render, and only the first may retry.
	const openedHtml = useRef<{value: string | undefined} | null>(null);
	// Whether a save has been handed to the save callback. From then on whether the stored
	// content matches is the consumer's to judge, not this hook's: the save may still fail.
	const handedOver = useRef(false);

	// One open per editor, not one per render. This is the denominator for every other
	// editor event: without it a fall in saves cannot be told apart from people simply
	// using the editor less.
	useEffect(() => {
		if (surface) {
			trackEditorOpened(surface);
		}
	}, [surface]);

	// The baseline is read only where blur is the save: elsewhere it is a serialisation
	// per editor mount for a string nothing reads, and a task modal mounts one editor per
	// comment.
	//
	// One task later, not in this effect: Plate fills the editor from a state update of
	// its own, so the editor still serialises as empty while the effects of the render
	// that mounted it are running. Nobody can type inside that window, and if the
	// capture is somehow missed the baseline stays unset, which reports as before rather
	// than going quiet.
	useEffect(() => {
		if (!onDraftJsChanged || openedHtml.current) return;
		const capture = window.setTimeout(() => {
			openedHtml.current = {value: getEditorHtml?.()};
		}, 0);
		return () => window.clearTimeout(capture);
	}, [onDraftJsChanged, getEditorHtml]);

	const checkForDrift = () => {
		if (!surface || driftChecked.current) return;
		driftChecked.current = true;

		const drift = detectRoundTripDrift(initialDraftJsContent);
		if (drift) {
			trackRoundTripDrift(surface, drift);
		}
	};

	// Before anything is handed over, an edit is content that differs from what the editor
	// opened with. After, any content may still need saving: the consumer decides.
	const isUnsavedEdit = (htmlContent: string): boolean => handedOver.current || htmlContent !== openedHtml.current?.value;

	const convertHtmlToDraftJs = (htmlContent: string) => {
		checkForDrift();

		// Blur is the save only on the surfaces that gave us a callback to save through.
		// Both comment editors convert on submit instead (convertEditedHtml.ts), so the
		// result here would be discarded — and the footer composer is never read-only, so
		// it blurs on any click-away. Reporting from here toasted the user about a save
		// they had not asked for and then counted the real failure a second time when they
		// pressed Save.
		if (!onDraftJsChanged) return;

		// An empty export is a serialisation that failed, not an editor the user cleared: a
		// cleared editor still holds its one empty paragraph and exports it as `<p></p>`.
		// The export renders the editor to read it, and returns '' when that render cannot
		// run, as when a pending click-outside export fires after the modal has unmounted.
		// Saving it would blank a description nobody touched.
		if (htmlContent === '') return;

		// Whether this blur is a save attempt at all. The description mutation skips
		// unchanged content (description_section.editTaskDescription), so `Save Succeeded`
		// needs an edit while a conversion failure did not — clicking in and out of a
		// description that cannot be converted read as a run of lost saves against no
		// successes, which is the shape the release plan's wave-1 gate looks at.
		//
		// Compared against the editor's own serialisation rather than `initialHtml`: the
		// two are never equal, because Plate adds its own classes and drops what it cannot
		// model, and there is no DraftJS left to compare once conversion has thrown.
		// `getEditorHtml` and `onBlur` are the same function inside the design system, so
		// the two strings are comparable.
		const contentChanged = isUnsavedEdit(htmlContent);

		// A blur that changed nothing saves nothing. The design system exports on every
		// click outside the editor, so this runs whenever the user touches another field —
		// the task name, a date, the close button — and the consumer's unchanged-check cannot
		// stop it for a description stored as html, as the data importer writes them: that
		// html never equals the DraftJS this produces, so each such click rewrote the
		// description, and the trip through the editor drops its line breaks.
		//
		// An empty editor is handed over even so. Consumers already treat blank on blank as
		// nothing to save, and some react to the blur itself: the client notes panel falls
		// back to its empty state when Create Note is blurred with nothing typed.
		if (!contentChanged && !isEditorEmpty(htmlContent)) return;

		let draftJsContent: string;
		try {
			draftJsContent = convertFromHtmlToDraftJs(htmlContent);
		} catch {
			// The edit exists only in the editor at this point. Returning without telling
			// anyone is what used to happen, and it discarded the edit in silence: the save
			// callback below is what reaches CommitMutation, so its correlation-ID error
			// modal never fired either. The converter has already reported the throw with
			// the content shape; what is left is to say so where the user can see it, and
			// to leave their text in the editor where they can still copy it.
			//
			// Deliberately not rethrown: this runs from the editor's onBlur, so an escaping
			// throw would take out the surrounding tree instead of the one field.
			createToast({
				duration: 8000,
				message: intl.formatMessage({id: 'rich_text_editor.changes_could_not_be_saved'}),
			});
			if (surface) {
				trackEditorSaveFailed(surface, 'conversion');
			}
			return;
		}

		onDraftJsChanged(draftJsContent);
		// Not a saved edit: the callback starts the save, it does not finish it. Had the
		// baseline moved on here, a mutation that then failed would leave every later blur
		// and the close comparing equal, and the edit would be lost on close. From here the
		// consumer's own unchanged-check decides: the stored content is now this editor's
		// DraftJS, which it can compare, and after a failure it is the old content again, so
		// the next blur resends. A failed conversion returns above and hands nothing over.
		handedOver.current = true;
	};

	// A missing baseline means the capture below has not run yet, which is the first task
	// after mount — nobody can have typed inside it, so there is nothing to rescue. A
	// captured-but-unreadable baseline (`value === undefined`) reads as changed instead,
	// because losing an edit is worse than a write that changes nothing.
	const hasUnexportedChange = (htmlContent: string) => openedHtml.current !== null && isUnsavedEdit(htmlContent);

	return {initialHtml, convertHtmlToDraftJs, hasUnexportedChange};
};
